import React from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { ClipboardList, Printer, Bell, LogOut } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import PosBackgroundServices from './PosBackgroundServices';
import autoPrintController from '../../services/printer/autoPrintController';
import newOrderPoller from '../../services/pos/newOrderPoller';

/**
 * POS shell — top bar + 3-tab bottom navigation.
 *
 * The POS device (Android WebView wrapper) uses this instead of the full
 * admin app. Only three surfaces are exposed:
 *   1. Orders   — today's tickets, status changes, manual reprint
 *   2. Printer  — built-in printer status + Test Print + auto-print toggle
 *   3. Alerts   — new-order notifications feed
 *
 * Everything else (menu, categories, delivery, finance, users, settings)
 * lives on the web-only /admin routes and is protected by verify_admin.
 */
const TABS = [
  { to: '/pos/orders', label: 'Orders', icon: ClipboardList, testid: 'pos-tab-orders' },
  { to: '/pos/printer', label: 'Printer', icon: Printer, testid: 'pos-tab-printer' },
  { to: '/pos/alerts', label: 'Alerts', icon: Bell, testid: 'pos-tab-alerts' },
];

export default function PosLayout() {
  const navigate = useNavigate();
  const { logout } = useAuth();

  const handleLogout = () => {
    // Explicit sign-out is the ONLY path that stops the background
    // singletons. Route transitions / React re-renders never do.
    try { autoPrintController.stop(); } catch (_e) { /* isolated */ }
    try { newOrderPoller.stop(); } catch (_e) { /* isolated */ }
    logout();
    navigate('/pos/login', { replace: true });
  };

  return (
    <div className="min-h-screen flex flex-col bg-neutral-50" data-testid="pos-shell">
      {/* Headless background services — auto-print controller + new-order chime. */}
      <PosBackgroundServices />

      {/* Top bar — minimal, non-scrollable, POS branding */}
      <header className="sticky top-0 z-30 h-14 flex items-center justify-between px-4 bg-white border-b border-neutral-200">
        <div className="font-semibold text-lg tracking-tight" data-testid="pos-shell-title">
          Cravin Crispy POS
        </div>
        <button
          type="button"
          onClick={handleLogout}
          className="flex items-center gap-1 text-sm text-neutral-600 hover:text-neutral-900 active:text-neutral-400"
          data-testid="pos-logout-btn"
          aria-label="Sign out of POS"
        >
          <LogOut className="h-4 w-4" />
          Sign out
        </button>
      </header>

      {/* Content area — flex-1 so it fills to the bottom nav */}
      <main className="flex-1 overflow-y-auto pb-20" data-testid="pos-shell-content">
        <Outlet />
      </main>

      {/* Bottom tab bar */}
      <nav
        className="fixed bottom-0 inset-x-0 h-16 bg-white border-t border-neutral-200 flex items-stretch z-40 shadow-[0_-2px_10px_rgba(0,0,0,0.04)]"
        data-testid="pos-bottom-nav"
      >
        {TABS.map(({ to, label, icon: Icon, testid }) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) =>
              `flex-1 flex flex-col items-center justify-center gap-0.5 text-[11px] font-medium transition-colors ${
                isActive
                  ? 'text-[#C83136]'
                  : 'text-neutral-500 hover:text-neutral-800'
              }`
            }
            data-testid={testid}
          >
            <Icon className="h-5 w-5" />
            {label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
